import React from 'react';
import type { Citation } from '../data/citations';
import { isRecognizedSourceUrl, inferSourceKind, sourceKindLabel } from '../data/citations';
import './Citations.css';

/**
 * The one citation renderer for the whole app (App Review 1.4.1).
 *
 * Every surface that states a medical fact renders this directly beneath the
 * claim it backs. Sources always render as tappable links; a source whose host
 * is not recognized is dropped rather than shown as text, because build 96 was
 * rejected for presenting un-tappable author-year text as citations.
 *
 * If this renders nothing, the claim above it is uncited - which is a bug, and
 * `citations.audit.test.ts` is what catches it.
 */

interface CitationsProps {
  sources: Citation[] | undefined;
  /** `inline` sits under a paragraph; `block` gets its own card on long-form pages. */
  variant?: 'inline' | 'block';
  /** Overrides the "Sources" label, e.g. "Source" for a single reference. */
  label?: string;
  className?: string;
}

export const Citations: React.FC<CitationsProps> = ({
  sources,
  variant = 'inline',
  label,
  className,
}) => {
  const linkable = (sources ?? []).filter((s) => isRecognizedSourceUrl(s.url));
  if (linkable.length === 0) return null;

  const heading = label ?? (linkable.length === 1 ? 'Source' : 'Sources');

  return (
    <div
      className={`citations citations-${variant}${className ? ` ${className}` : ''}`}
      data-testid="citations"
    >
      <span className="citations-label">{heading}</span>
      <ul className="citations-list">
        {linkable.map((s, i) => {
          const kind = sourceKindLabel(s.kind ?? inferSourceKind(s.url));
          return (
            <li key={`${s.url}-${i}`} className="citations-item">
              <a
                className="citations-link"
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {s.name}
                <ExternalGlyph />
              </a>
              {kind && <span className="citations-kind">{kind}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ExternalGlyph: React.FC = () => (
  <svg
    className="citations-external"
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
  </svg>
);

export default Citations;
